/**
 * Two gaps in Safari that stop a document from being read, and therefore from
 * being signed.
 *
 * A form link is public: it is opened on whatever Mac or iPhone the person
 * happens to have, and a browser two versions behind is normal. Both of these
 * are small, exact implementations of what the standard already specifies, and
 * both no-op on a browser that has the feature.
 *
 * Imported first in main.tsx, before anything that can reach pdf.js.
 */

/**
 * TypeScript's DOM library does not declare async iteration on ReadableStream,
 * because not every browser has it. This file is what makes it true here, so
 * it is also the right place to say so.
 */
declare global {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- must match lib.dom's own parameter exactly
  interface ReadableStream<R = any> {
    values(options?: { preventCancel?: boolean }): AsyncIterableIterator<R>;
    [Symbol.asyncIterator](): AsyncIterableIterator<R>;
  }
}

/**
 * `ReadableStream` async iteration — Safari 18.4.
 *
 * pdf.js reads the text layer with `for await (const chunk of stream)`. Without
 * the async iterator that is `undefined is not a function`, thrown inside
 * `getTextContent`, which took down the whole signing page on Safari 18.3.
 *
 * Follows the Streams standard's `values(options)`: release the reader when the
 * stream ends or throws, and cancel it when the loop is abandoned early, unless
 * the caller asked otherwise.
 */
export function installReadableStreamAsyncIterator(): void {
  if (typeof ReadableStream === 'undefined') return;

  const prototype = ReadableStream.prototype as unknown as Record<string | symbol, unknown>;
  if (typeof prototype[Symbol.asyncIterator] === 'function') return;

  function values<T>(
    this: ReadableStream<T>,
    { preventCancel = false }: { preventCancel?: boolean } = {},
  ): AsyncIterableIterator<T> {
    const reader = this.getReader();

    return {
      async next(): Promise<IteratorResult<T>> {
        try {
          const result = await reader.read();
          if (result.done) {
            reader.releaseLock();
            return { done: true, value: undefined };
          }
          return { done: false, value: result.value };
        } catch (error) {
          reader.releaseLock();
          throw error;
        }
      },

      async return(value?: unknown): Promise<IteratorResult<T>> {
        if (preventCancel) {
          reader.releaseLock();
        } else {
          const cancelled = reader.cancel(value);
          reader.releaseLock();
          await cancelled;
        }
        return { done: true, value: undefined };
      },

      [Symbol.asyncIterator](): AsyncIterableIterator<T> {
        return this;
      },
    } as AsyncIterableIterator<T>;
  }

  if (typeof prototype.values !== 'function') {
    Object.defineProperty(prototype, 'values', {
      value: values,
      writable: true,
      configurable: true,
    });
  }
  Object.defineProperty(prototype, Symbol.asyncIterator, {
    value: prototype.values,
    writable: true,
    configurable: true,
  });
}

/**
 * `Promise.withResolvers` — Safari 17.4.
 *
 * pdf.js uses it throughout, so on an older Safari the document fails long
 * before the text layer. Not what the reported crash was, but the same link
 * reaches those browsers too.
 */
export function installPromiseWithResolvers(): void {
  const target = Promise as unknown as Record<string, unknown>;
  if (typeof target.withResolvers === 'function') return;

  Object.defineProperty(target, 'withResolvers', {
    value: function withResolvers<T>() {
      let resolve!: (value: T | PromiseLike<T>) => void;
      let reject!: (reason?: unknown) => void;
      const promise = new Promise<T>((res, rej) => {
        resolve = res;
        reject = rej;
      });
      return { promise, resolve, reject };
    },
    writable: true,
    configurable: true,
  });
}

installReadableStreamAsyncIterator();
installPromiseWithResolvers();
